import { reads, alias, notEmpty, and, not, equal } from '@ember/object/computed';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { computed, set } from '@ember/object';
import EmberDataBaseModel from '@square/dashboard-data/models/ember-data-base-model';
import { condition, notSameProperty } from '@square/dashboard-utilities/macros';
import Subscription from '@square/dashboard-data/models/subscription';
import { inject as service } from '@ember/service';
import { attr } from '@ember-data/model';

// Readonly object representing the current billing state for this merchant
export default EmberDataBaseModel.extend({
  intl: service(),
  userInfo: service(),
  // Actual model properties from the server
  billingSources: fragmentArray('subscription-payment-source'),
  suggestedSourcesV2: fragment('subscription-payment-sources-list'),
  token: attr('string'),
  // Computed properties that make it easier to consume this model
  billingSource: reads('billingSources.firstObject'),
  currentBillingType: reads('billingSource.type'),
  savedPaymentMethod: alias('currentBillingType'),
  userToken: reads('id'),
  banks: alias('suggestedSourcesV2.banks'),
  bankAccount: condition('hasBillingSourceBankAccount', {
    then: 'billingSource.bankAccount',
    else: 'banks.firstObject'
  }),
  hasABankAccount: notEmpty('suggestedSourcesV2.banks'),
  hasBillingSource: notEmpty('billingSource'),
  hasBillingSourceBankAccount: reads('billingSource.isBankAccount'),
  canSelectPayoutAdjustment: and('userInfo.isUS', 'hasABankAccount'),
  cannotSelectPayoutAdjustment: not('canSelectPayoutAdjustment'),
  paymentMethod: computed('_paymentMethod', 'defaultPaymentMethod', 'savedPaymentMethod', {
    get() {
      if (this._paymentMethod !== undefined) {
        return this._paymentMethod;
      }
      return this.initialPaymentMethod();
    },
    set(_, value) {
      return set(this, '_paymentMethod', value);
    }
  }),
  isConfigurationForUnit: notSameProperty('userToken', 'userInfo.token'),
  isPayoutAdjustmentSelected: equal('paymentMethod', 'payout_adjustment'),
  isCardSelected: equal('paymentMethod', 'card'),
  shouldValidate: computed('isCardSelected', 'hasBillingSource', function () {
    return this.isCardSelected && !this.hasBillingSource;
  }),
  shouldShowBankPicker: not('isConfigurationForUnit'),
  description: computed('hasBillingSource', 'billingSource.typeAsString', function () {
    if (this.hasBillingSource) {
      return this.billingSource?.typeAsString;
    } else {
      return this.intl.t('subscriptions.sub2.paymentMethods.none.statement');
    }
  }),
  descriptionShort: computed('hasBillingSource', 'billingSource.typeAsStringShort', function () {
    if (this.hasBillingSource) {
      return this.billingSource?.typeAsStringShort;
    } else {
      return this.intl.t('subscriptions.sub2.paymentMethods.none.statementShort');
    }
  }),
  defaultPaymentMethod: computed('cannotSelectPayoutAdjustment', function () {
    if (this.cannotSelectPayoutAdjustment) {
      return Subscription.paymentMethods.card;
    } else {
      return Subscription.paymentMethods.payout_adjustment;
    }
  }),
  clearCachedBillingSource() {
    this.billingSources.clear();
  },
  replaceCachedBillingSource(newSource) {
    this.clearCachedBillingSource();
    this.billingSources.addObject(newSource);
  },
  initialPaymentMethod() {
    if (this.savedPaymentMethod) {
      return this.savedPaymentMethod;
    } else {
      return this.defaultPaymentMethod;
    }
  },
  resetPaymentMethod() {
    this.set('paymentMethod', this.initialPaymentMethod());
  }
}).reopenClass({
  _modelNameForMigration: 'subscription-payment-configuration',
  url: '/api/v1/subscriptions/payment-configuration',
  rootKey: 'entity',
  collectionKey: 'entities'
});